import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

interface SideMenuItem {
  label: string;
  path?: string;
  icon: string;
  children?: SideMenuItem[];
  isExternal?: boolean;
}

interface SideMenuProps {
  isOpen: boolean;
  onClose: () => void;
  isEnglish: boolean;
}

const SideMenu = ({ isOpen, onClose }: SideMenuProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isEnglish = location.pathname === '/en' || location.pathname === '/en/' || location.pathname.startsWith('/en/');
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [isLangOpen, setIsLangOpen] = useState(false);

  const withEn = (path: string): string => {
    if (!isEnglish) return path;
    if (!path.startsWith('/')) return path;
    if (path.startsWith('/en')) return path;
    if (path.startsWith('/blog')) return path;
    if (path.startsWith('http')) return path;
    if (path.startsWith('#')) return path;
    if (path === '/') return '/en';
    return '/en' + path;
  };

  const toEnglish = (pathname: string): string => {
    if (pathname.startsWith('/blog')) return pathname;
    if (pathname === '/en' || pathname.startsWith('/en/')) return pathname;
    if (pathname === '/') return '/en';
    return '/en' + pathname;
  };

  const toJapanese = (pathname: string): string => {
    if (pathname.startsWith('/blog')) return pathname;
    if (pathname === '/en') return '/';
    if (pathname.startsWith('/en/')) return pathname.substring(3);
    return pathname;
  };

  const switchLanguage = (lang: 'ja' | 'en') => {
    const targetPath = lang === 'en' ? toEnglish(location.pathname) : toJapanese(location.pathname);
    const fullPath = targetPath + location.search + location.hash;
    navigate(fullPath);
    onClose();
  };

  // アクティブ判定：完全一致またはサブパス判定（外部リンクも含む）
  const isActivePath = (itemPath?: string) => {
    if (!itemPath) return false;
    if (itemPath === '/') {
      return location.pathname === '/' || location.pathname === '/en' || location.pathname === '/en/';
    }
    // /blog/ の場合も判定に含める
    if (itemPath === '/blog') {
      return location.pathname === '/blog' || location.pathname.startsWith('/blog/');
    }
    const checkPath = itemPath.replace(/^\/en/, '');
    const currentPath = location.pathname.replace(/^\/en/, '');
    return currentPath === checkPath || currentPath.startsWith(checkPath + '/');
  };

  const menuItems: SideMenuItem[] = [
    { label: isEnglish ? 'Home' : 'ホーム', path: '/', icon: 'ri-home-line' },
    {
      label: isEnglish ? 'Filament Data' : 'フィラメントデータ',
      path: '/filament',
      icon: 'ri-database-line',
      children: [
        { label: isEnglish ? 'Filament Compare' : 'フィラメント比較', path: '/filament/compare', icon: 'ri-bar-chart-horizontal-line' },
        {
          label: 'PLA',
          path: '/filament/pla',
          icon: 'ri-file-list-line',
          children: [
            { label: 'PLA', path: '/filament/pla', icon: 'ri-file-text-line' },
            { label: 'PLA+', path: '/filament/pla-plus', icon: 'ri-file-text-line' },
            { label: 'Matte PLA', path: '/filament/matte-pla', icon: 'ri-file-text-line' },
            { label: 'Silk PLA', path: '/filament/pla-silk', icon: 'ri-file-text-line' },
            { label: 'PLA-CF', path: '/filament/pla-cf', icon: 'ri-file-text-line' },
            { label: 'PLA PRO', path: '/filament/pla-pro', icon: 'ri-file-text-line' },
            { label: 'HT-PLA', path: '/filament/ht-pla', icon: 'ri-file-text-line' }
          ]
        },
        {
          label: 'PETG',
          path: '/filament/petg',
          icon: 'ri-file-list-line',
          children: [
            { label: 'PETG', path: '/filament/petg', icon: 'ri-file-text-line' },
            { label: 'PETG-CF', path: '/filament/petg-cf', icon: 'ri-file-text-line' },
            { label: 'PETG-GF', path: '/filament/petg-gf', icon: 'ri-file-text-line' }
          ]
        },
        {
          label: 'ABS',
          path: '/filament/abs',
          icon: 'ri-file-list-line',
          children: [
            { label: 'ABS', path: '/filament/abs', icon: 'ri-file-text-line' },
            { label: 'ABS-CF', path: '/filament/abs-cf', icon: 'ri-file-text-line' },
            { label: 'ABS-GF', path: '/filament/abs-gf', icon: 'ri-file-text-line' }
          ]
        },
        {
          label: 'ASA',
          path: '/filament/asa',
          icon: 'ri-file-list-line',
          children: [
            { label: 'ASA', path: '/filament/asa', icon: 'ri-file-text-line' },
            { label: 'ASA-CF', path: '/filament/asa-cf', icon: 'ri-file-text-line' },
            { label: 'ASA-GF', path: '/filament/asa-gf', icon: 'ri-file-text-line' }
          ]
        },
        {
          label: 'PC',
          path: '/filament/pc',
          icon: 'ri-file-list-line',
          children: [
            { label: 'PC', path: '/filament/pc', icon: 'ri-file-text-line' },
            { label: 'PC-CF', path: '/filament/pc-cf', icon: 'ri-file-text-line' },
            { label: 'PC-GF', path: '/filament/pc-gf', icon: 'ri-file-text-line' }
          ]
        },
        {
          label: isEnglish ? 'PA (Nylon)' : 'PA',
          path: '/filament/pa',
          icon: 'ri-file-list-line',
          children: [
            { label: isEnglish ? 'PA (Nylon)' : 'PA (ナイロン)', path: '/filament/pa', icon: 'ri-file-text-line' },
            { label: 'PA-CF', path: '/filament/pa-cf', icon: 'ri-file-text-line' },
            { label: 'PA-GF', path: '/filament/pa-gf', icon: 'ri-file-text-line' }
          ]
        },
        { label: 'TPU', path: '/filament/tpu', icon: 'ri-file-text-line' }
      ]
    },
    {
      label: isEnglish ? 'For Beginners' : '初心者向け',
      path: '/beginner',
      icon: 'ri-graduation-cap-line'
    },
    {
      label: isEnglish ? 'Useful Knowledge' : '役立ち関連知識',
      path: '/knowledge',
      icon: 'ri-lightbulb-line'
    },
    {
      label: isEnglish ? 'Troubleshooting' : 'トラブル対策',
      path: '/troubleshooting',
      icon: 'ri-tools-line'
    },
    {
      label: isEnglish ? 'Bending Strength Test Method' : '曲げ強度試験方法',
      path: '/filament/bend-strength-test',
      icon: 'ri-flask-line'
    },
    {
      label: isEnglish ? 'Blog' : 'ブログ',
      path: '/blog',
      icon: 'ri-article-line'
    }
  ];

  // 現在のパスに基づいて初期展開状態を設定
  useEffect(() => {
    const findParentKeys = (items: SideMenuItem[], parentKey: string = ''): string[] => {
      const keys: string[] = [];
      
      items.forEach(item => {
        const itemKey = parentKey ? `${parentKey}-${item.label}` : item.label;
        
        if (item.children) {
          // 子要素のいずれかがアクティブなら、この親を展開
          const hasActiveChild = item.children.some(child => 
            isActivePath(child.path) || 
            (child.children && child.children.some(grandChild => isActivePath(grandChild.path)))
          );
          
          if (hasActiveChild || isActivePath(item.path)) {
            keys.push(itemKey);
          }
          
          // 再帰的に子要素もチェック
          keys.push(...findParentKeys(item.children, itemKey));
        }
      });
      
      return keys;
    };

    const initialExpandedKeys = findParentKeys(menuItems);
    setExpandedItems(initialExpandedKeys);
  }, [location.pathname, isEnglish]);

  const toggleSection = (sectionKey: string) => {
    setExpandedItems(prev => {
      if (prev.includes(sectionKey)) {
        return prev.filter(item => item !== sectionKey);
      } else {
        return [...prev, sectionKey];
      }
    });
  };

  const renderMenuItem = (item: SideMenuItem, level: number = 0, parentKey: string = '') => {
    const itemKey = parentKey ? `${parentKey}-${item.label}` : item.label;
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.includes(itemKey);
    const isActive = isActivePath(item.path);
    const paddingLeft = level === 0 ? 'pl-3' : level === 1 ? 'pl-6' : 'pl-9';

    if (hasChildren) {
      return (
        <div key={itemKey}>
          <div className="flex items-center">
            {item.path ? (
              item.isExternal ? (
                <a
                  href={item.path}
                  className={`flex-1 flex items-center space-x-3 ${paddingLeft} py-2 text-sm transition-all ${
                    isActive
                      ? 'text-accent bg-metal font-medium'
                      : 'text-metal-light hover:text-white hover:bg-metal'
                  }`}
                  onClick={onClose}
                >
                  <i className={`${item.icon} text-lg w-5 h-5 flex items-center justify-center`}></i>
                  <span>{item.label}</span>
                </a>
              ) : (
                <Link
                  to={withEn(item.path)}
                  className={`flex-1 flex items-center space-x-3 ${paddingLeft} py-2 text-sm transition-all ${
                    isActive
                      ? 'text-accent bg-metal font-medium'
                      : 'text-metal-light hover:text-white hover:bg-metal'
                  }`}
                  onClick={onClose}
                >
                  <i className={`${item.icon} text-lg w-5 h-5 flex items-center justify-center`}></i>
                  <span>{item.label}</span>
                </Link>
              )
            ) : (
              <div className={`flex-1 flex items-center space-x-3 ${paddingLeft} py-2 text-sm text-metal-light`}>
                <i className={`${item.icon} text-lg w-5 h-5 flex items-center justify-center`}></i>
                <span>{item.label}</span>
              </div>
            )}
            <button
              onClick={() => toggleSection(itemKey)}
              className="w-8 h-8 flex items-center justify-center text-metal-light hover:text-white transition-colors"
              aria-label={isExpanded ? (isEnglish ? 'Close' : '閉じる') : (isEnglish ? 'Open' : '開く')}
            >
              <i className={`${isExpanded ? 'ri-subtract-line' : 'ri-add-line'} text-lg`}></i>
            </button>
          </div>
          {isExpanded && (
            <div className="ml-0">
              {item.children.map(child => renderMenuItem(child, level + 1, itemKey))}
            </div>
          )}
        </div>
      );
    }

    if (item.isExternal) {
      return (
        <a
          key={itemKey}
          href={item.path}
          className={`flex items-center space-x-3 ${paddingLeft} py-2 text-sm transition-all ${
            isActive
              ? 'text-accent bg-metal font-medium'
              : 'text-metal-light hover:text-white hover:bg-metal'
          }`}
          onClick={onClose}
        >
          <i className={`${item.icon} text-lg w-5 h-5 flex items-center justify-center`}></i>
          <span>{item.label}</span>
        </a>
      );
    }

    return (
      <Link
        key={itemKey}
        to={withEn(item.path!)}
        className={`flex items-center space-x-3 ${paddingLeft} py-2 text-sm transition-all ${
          isActive
            ? 'text-accent bg-metal font-medium'
            : 'text-metal-light hover:text-white hover:bg-metal'
        }`}
        onClick={onClose}
      >
        <i className={`${item.icon} text-lg w-5 h-5 flex items-center justify-center`}></i>
        <span>{item.label}</span>
      </Link>
    );
  };

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 backdrop-blur-sm"
          onClick={onClose}
        ></div>
      )}

      {/* Side Menu */}
      <aside
        className={`fixed top-0 left-0 h-full w-80 bg-metal-dark border-r border-metal z-50 transform transition-transform duration-300 ease-in-out overflow-y-auto ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="p-6">
          {/* Logo and Site Title */}
          <div className="flex items-center space-x-3 mb-8 pb-6 border-b border-metal">
            <img 
              src="/assets/nag-logo-512.png" 
              alt={isEnglish ? 'Sekisou NAG Note Logo' : 'せきそう NAG ノート ロゴ'}
              className="w-10 h-10 object-contain"
            />
            <div className="flex flex-col">
              <span className="text-white text-lg font-bold font-['Exo'] tracking-wide">
                せきそうNAGノート
              </span>
              <span className="text-metal-light text-xs font-['Roboto_Mono']">
                FDM 3D Printer Lab
              </span>
            </div>
          </div>

          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-metal-light hover:text-white transition-colors"
            aria-label={isEnglish ? 'Close menu' : 'メニューを閉じる'}
          >
            <i className="ri-close-line text-2xl"></i>
          </button>

          {/* Menu Items */}
          <nav className="space-y-1">
            {menuItems.map(item => renderMenuItem(item))}

            {/* Language Settings */}
            <div>
              <div className="flex items-center">
                <button
                  onClick={() => setIsLangOpen(!isLangOpen)}
                  className="flex-1 flex items-center space-x-3 pl-3 py-2 text-sm text-metal-light hover:text-white hover:bg-metal transition-all"
                >
                  <i className="ri-global-line text-lg w-5 h-5 flex items-center justify-center"></i>
                  <span>言語設定 / Language</span>
                </button>
                <button
                  onClick={() => setIsLangOpen(!isLangOpen)}
                  className="w-8 h-8 flex items-center justify-center text-metal-light hover:text-white transition-colors"
                  aria-label={isLangOpen ? (isEnglish ? 'Close' : '閉じる') : (isEnglish ? 'Open' : '開く')}
                >
                  <i className={`${isLangOpen ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'} text-lg`}></i>
                </button>
              </div>
              {isLangOpen && (
                <div className="ml-0">
                  <button
                    onClick={() => switchLanguage('ja')}
                    className={`w-full flex items-center space-x-3 pl-6 py-2 text-sm transition-all ${
                      !isEnglish
                        ? 'text-accent bg-metal font-medium'
                        : 'text-metal-light hover:text-white hover:bg-metal'
                    }`}
                  >
                    <i className="ri-translate-2 text-lg w-5 h-5 flex items-center justify-center"></i>
                    <span>日本語</span>
                  </button>
                  <button
                    onClick={() => switchLanguage('en')}
                    className={`w-full flex items-center space-x-3 pl-6 py-2 text-sm transition-all ${
                      isEnglish
                        ? 'text-accent bg-metal font-medium'
                        : 'text-metal-light hover:text-white hover:bg-metal'
                    }`}
                  >
                    <i className="ri-translate-2 text-lg w-5 h-5 flex items-center justify-center"></i>
                    <span>English</span>
                  </button>
                </div>
              )}
            </div>
          </nav>
        </div>
      </aside>
    </>
  );
};

export default SideMenu;
